import { useState } from "react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import type { AnswerForType, Enduser, Pharmacy } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import type { TellescopeFieldResponse } from "../../types";
import { getLatestAddressAnswer } from "../../utils";

interface UsePharmacySearchFieldArgs {
  value: AnswerForType["Pharmacy Search"];
  onChange: (newVal: AnswerForType["Pharmacy Search"]) => void;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  formResponses: TellescopeFieldResponse[];
}

const isPharmacyArray = (value: unknown): value is Pharmacy[] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) => typeof item === "object" && item !== null && "ncpdpId" in item && typeof item.ncpdpId === "string"
  );
};

export const usePharmacySearchField = ({ value, onChange, enduser, formResponses }: UsePharmacySearchFieldArgs) => {
  const { t } = useTranslation("forms");
  const addressAnswer = getLatestAddressAnswer(formResponses);

  const derivedZip = addressAnswer?.zipCode ?? enduser?.zipCode ?? "";

  const [zipCode, setZipCodeRaw] = useState(derivedZip);
  const [searchZip, setSearchZip] = useState<string | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const [appliedZip, setAppliedZip] = useState(derivedZip);

  // Both prefill sources resolve asynchronously (the enduser query, and form responses that may be
  // database-prepopulated), so the value is usually absent on first render. Adjust during render
  // rather than in an effect, and never once the patient has typed — their input always wins.
  if (!hasEdited && derivedZip !== appliedZip) {
    setAppliedZip(derivedZip);
    if (derivedZip) setZipCodeRaw(derivedZip);
  }

  const setZipCode = (raw: string) => {
    const digitsOnly = raw.replace(/\D/g, "").slice(0, 5);
    setHasEdited(true);
    setZipCodeRaw(digitsOnly);
  };

  const searchQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "ScriptSure",
      type: "pharmacy-search",
      query: searchZip ? JSON.stringify({ zipCode: searchZip }) : undefined,
    },
    enabled: !!searchZip,
  });

  const search = () => {
    if (zipCode.length !== 5) return;
    setHasSearched(true);
    setSearchZip(zipCode);
  };

  const canSearch = zipCode.length === 5;
  const isLoading = searchQuery.isFetching;

  const rawData = searchQuery.data?.data;
  const results: Pharmacy[] = isPharmacyArray(rawData) ? rawData : [];

  let searchError: string | undefined;
  if (hasSearched && !isLoading) {
    if (searchQuery.error) {
      searchError = t("Unable to search pharmacies. Please try again.");
    } else if (results.length === 0 && searchZip) {
      searchError = t("No pharmacies found for this ZIP code");
    }
  }

  const select = (pharmacy: Pharmacy) => {
    onChange(pharmacy);
  };

  const clear = () => {
    onChange(undefined);
  };

  return {
    zipCode,
    setZipCode,
    canSearch,
    search,
    isLoading,
    searchError,
    results: value ? [] : results,
    selected: value,
    select,
    clear,
    hasSearched,
  };
};
